import create from 'zustand';
import { devtools } from 'zustand/middleware';
import { AbeConfirmPayload } from '../../api/abe';

export type AbeMessageRole = 'user' | 'assistant';

export interface AbeMessage {
  chartData?: any;
  confirmationPayload?: AbeConfirmPayload;
  content: string;
  id: string;
  isStreaming?: boolean;
  requiresConfirmation?: boolean;
  role: AbeMessageRole;
  timestamp: Date;
  toolCalls?: string[];
}

// Matches AbeSavedChartRecord from api/abe.ts exactly
export interface SavedAbeChart {
  chartData: any;
  createdAt: string;
  dashboardId: string;
  id: string;
  originalSql: string;
  title: string;
  updatedAt: string;
}

export type AbeView = 'chat' | 'ideas';

interface AbeState {
  abortController: AbortController | null;
  activeChart: any | null;
  addMessage: (msg: Omit<AbeMessage, 'id' | 'timestamp'>) => string;
  appendToMessage: (id: string, chunk: string) => void;
  clearMessages: () => void;
  closeAbe: () => void;
  closeChart: () => void;
  isOpen: boolean;
  isStreaming: boolean;
  messages: AbeMessage[];
  newSession: () => void;
  openAbe: () => void;
  openChart: (data: any) => void;
  pendingConfirmation: AbeConfirmPayload | null;
  prefillMessage: string | null;
  removeChart: (id: string) => void;
  savedCharts: SavedAbeChart[];
  sessionId: string;
  setAbortController: (controller: AbortController | null) => void;
  setPendingConfirmation: (payload: AbeConfirmPayload | null) => void;
  setPrefillMessage: (message: string | null) => void;
  setSavedCharts: (charts: SavedAbeChart[]) => void;
  setStreaming: (val: boolean) => void;
  setThinkingStatus: (status: string) => void;
  setView: (view: AbeView) => void;
  thinkingStatus: string;
  toggleAbe: () => void;
  updateMessage: (id: string, updates: Partial<AbeMessage>) => void;
  view: AbeView;
}

const generateId = () =>
  `abe-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const useAbeStore = create<AbeState>()(
  devtools(
    (set) => ({
      abortController: null,
      activeChart: null,
      addMessage: (msg) => {
        const id = generateId();
        set((s) => ({
          messages: [...s.messages, { ...msg, id, timestamp: new Date() }]
        }));
        return id;
      },
      appendToMessage: (id, chunk) =>
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === id ? { ...m, content: m.content + chunk } : m
          )
        })),
      clearMessages: () => set({ messages: [] }),
      closeAbe: () => set({ isOpen: false }),
      closeChart: () => set({ activeChart: null }),
      isOpen: false,
      isStreaming: false,
      messages: [],
      newSession: () =>
        set({
          messages: [],
          pendingConfirmation: null,
          sessionId: generateId()
        }),
      openAbe: () => set({ isOpen: true }),
      openChart: (data) => set({ activeChart: data }),
      pendingConfirmation: null,
      prefillMessage: null,
      removeChart: (id) =>
        set((s) => ({
          savedCharts: s.savedCharts.filter((c) => c.id !== id)
        })),
      savedCharts: [],
      sessionId: generateId(),
      setAbortController: (controller) => set({ abortController: controller }),
      setPendingConfirmation: (payload) =>
        set({ pendingConfirmation: payload }),
      setPrefillMessage: (message) => set({ prefillMessage: message }),
      setSavedCharts: (charts) => set({ savedCharts: charts }),
      setStreaming: (val) => set({ isStreaming: val }),
      setThinkingStatus: (status) => set({ thinkingStatus: status }),
      setView: (view) => set({ view }),
      thinkingStatus: '',
      toggleAbe: () => set((s) => ({ isOpen: !s.isOpen })),
      updateMessage: (id, updates) =>
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === id ? { ...m, ...updates } : m
          )
        })),
      view: 'chat'
    }),
    { name: 'AbeStore' }
  )
);

export default useAbeStore;
